/**
 * MedicationSafetyGate.tsx — what this medicine can do to you, before you pay.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: medication safety
 * (2026-08-19).
 *
 * WHY THIS EXISTS. A clinician (Reneal Patel, 2026-08-18) raised that a patient
 * could commit money to a prescription-only weight-loss medicine having been
 * shown only two things: what it costs, and how much weight they might lose.
 * Nothing on the way to the card form told them what it can do to them, what to
 * watch for, or when to stop and get help. That is not a UX gap — for a
 * prescriber it is the difference between informed consent and a sale.
 *
 * WHY IT IS NOT A NEW STEP. The CTO was explicit: "I don't want to hinder the
 * journey flow or make it difficult, but that information should be available
 * for the patients to read and understand." So this adds no screen and no click
 * to the funnel: it renders in place on the payment screen, above the pay
 * control, and the full detail is one press away on a public page the patient
 * can keep open in a second tab while they pay.
 *
 * WHY THERE IS EXACTLY ONE CONTROL (2026-08-20). This block used to carry three
 * routes to the same content where one would do: a bulleted list of every topic
 * title, a button that opened the detail in a MODAL, and a second link to the
 * public page. The CTO cut it to one — "can't we do 'read full safety
 * information' here without showing all conditions and just have one CTA only,
 * opening that in another page. Modal is not needed."
 *
 * The modal was the weaker of the two routes anyway. It was built from a
 * variable this screen happened to be holding, so it could not be bookmarked,
 * re-opened tomorrow, sent in a dispatch email, or shown to the family member a
 * patient wants to read it with — and escaping it meant dismissing a dialog
 * stacked on a half-finished checkout. A page has an address; a dialog has a
 * lifetime. On clinical content that difference is the whole point.
 *
 * WHAT WAS NOT MOVED BEHIND THE CLICK. The saving here is LENGTH, not
 * disclosure. Everything time-critical still renders in place with no press and
 * no request — see rule 1. Shortening a safety block by hiding the urgent lines
 * would be the same edit with the opposite meaning.
 *
 * THE FOUR RULES THIS COMPONENT ENFORCES, and the failure each one prevents:
 *
 *  1. THE URGENT LINES ARE NEVER BEHIND A DISCLOSURE. `redFlags` renders in
 *     full, always. A patient who reads exactly one thing on this screen must
 *     read the one that says "stop and get help today" — collapsing it behind a
 *     "show more" is how the only genuinely time-critical wording on the page
 *     becomes the wording nobody sees.
 *
 *  2. THE ACKNOWLEDGEMENT STILL RECORDS EVERY TOPIC KEY, even though the titles
 *     are no longer itemised on this screen (2026-08-20). This is the trap in
 *     the CTO's edit and it is worth being explicit about, because the obvious
 *     way to delete a visible list is to delete the `.map()` that draws it —
 *     and one of those two `.map()` calls was never about drawing anything.
 *
 *     `topicKeys` is NOT read back off the rendered list. {@link safetyAckFor}
 *     derives it from the same wire object the list was drawn from, so removing
 *     the list leaves the evidence byte-for-byte identical. It HAS to: orders
 *     stores those keys as the record of what safety content this order was sold
 *     against, and it would accept an acknowledgement whose `topicKeys` had
 *     quietly become `[]` — no error, no 400, just a stored claim that this
 *     patient was offered nothing. Nobody would find that until a complaint or
 *     an MHRA question asked what a specific patient was shown, by which point
 *     every order placed since the "tidy-up" carries the same hole.
 *
 *     WHAT THE KEYS NOW ATTEST, precisely, because the claim did change shape:
 *     not "these titles were itemised on the payment screen", but "this exact
 *     version of the safety content was on offer to this patient, one press
 *     away, at the moment they ticked". That is a weaker claim about reading and
 *     a stronger one about availability — and unlike the old in-place list it is
 *     checkable after the fact by anyone, because the press leads to a published
 *     address ({@link CLINICAL_SAFETY_PATH}) rather than to a dialog assembled
 *     from a variable that no longer exists.
 *
 *  3. NO CONTENT ⇒ NO SALE. `safety` is optional on the wire purely so a
 *     prices-service mid-rollout cannot 500 this screen. It is not a degraded
 *     mode we quietly trade through: when it is missing, or the read failed, the
 *     pay control is blocked and the reason is stated in plain words. Selling a
 *     prescription-only medicine unexplained is worse than losing the order.
 *
 *  4. THE BLOCK IS EXPLAINED IN TEXT, TIED TO THE CONTROL. Never colour alone
 *     and never a bare `disabled` attribute (WCAG 2.1 AA 1.4.1, 3.3.1): a patient
 *     staring at a dead button with no reason has no way to fix it. The reason
 *     paragraph is rendered beside the pay control by the caller and referenced
 *     with `aria-describedby` — the same structure `CommitmentTerms` +
 *     `PlanSelection` already established for the commitment gate, deliberately
 *     reused rather than a second pattern invented alongside it.
 *
 * THE PATIENT'S RENDERERS ARE EXPORTED, NOT COPIED (2026-08-20). This file is
 * the ONE place the patient-facing safety markup is written, and three surfaces
 * now mount it:
 *
 *   - this gate, on the payment screen;
 *   - clinicians/SafetyReview.tsx, where the clinical lead ratifies the wording
 *     (`safety.clinicallyApproved`). He is signing off WHAT A PATIENT SEES; a
 *     review screen with its own renderer is a review of something no patient is
 *     shown, and the day either side is restyled the sign-off silently stops
 *     covering the live screen;
 *   - safety/ClinicalSafetyPage.tsx, the public page this block's ONE control
 *     opens. The CTO's brief for it was explicit — it carries THIS urgent-action
 *     block, not something that resembles it — which is why
 *     `PatientUrgentActions` is a component here rather than JSX inlined into
 *     the gate.
 *
 * SO: SOME EXPORTS BELOW HAVE NO CALLER IN THIS FILE, AND THAT IS CORRECT.
 * Deleting the modal left `PatientSafetyDetail`, `PatientSafetyTopics` and
 * `PatientExternalSources` with no use inside the gate. They are not dead — they
 * are what the clinician review surface and the public page mount, and an editor
 * that flags them as unused is reading one file of a three-surface contract.
 * Removing one does not shrink this file; it blanks a screen nobody is looking
 * at while they work on checkout.
 *
 * Adding a fourth surface means importing from here. It never means writing the
 * markup again: two renderers of the same clinical content are two things that
 * can disagree, and nobody finds out which one a patient read.
 *
 * R28: every word of clinical content here arrives ON THE WIRE and is rendered
 * as given. No medication name, brand, class or delivery-form word is written in
 * this file — the R28 source scan (journey/r28.spec.tsx) reads comments too.
 */
import type {
  MedicationSafetyContent,
  MedicationSafetyExternalLink,
  MedicationSafetyTopic,
  SafetyAcknowledgement,
} from '../api/types';
import { withBasename } from '../config';
import { CLINICAL_SAFETY_PATH } from '../safety/route';
import { Alert, Button, WarningIcon } from '../ui/kit';

/**
 * What the payment screen knows about this medication's safety content.
 *
 * A DISCRIMINATED UNION rather than `MedicationSafetyContent | null`, for the
 * same reason `KeyAlerts` refuses a nullable array: a bare null collapses "still
 * loading", "the read failed" and "this medication genuinely has no content"
 * into one falsy value, and the three want different screens. Two of them block
 * the sale and one of them is temporary — a component that cannot tell them
 * apart either blocks a patient who was only waiting, or sells to one whose
 * read never answered.
 */
export type MedicationSafetyView =
  | { state: 'loading' }
  | { state: 'ready'; safety: MedicationSafetyContent | null }
  | { state: 'error' };

/** The id the caller puts on its blocked-reason paragraph and `aria-describedby`. */
export const PAY_BLOCKED_REASON_ID = 'pay-blocked-reason';

/**
 * The acknowledgement to send with checkout, built from the content that was ON
 * OFFER on this screen.
 *
 * DERIVED FROM THE WIRE OBJECT, NOT FROM THE DOM, and that survived the
 * 2026-08-20 trim deliberately. The gate no longer itemises the topic titles in
 * place — the CTO cut that list — but the keys recorded here did not change and
 * must not: they are the same `safety` object the gate rendered its urgent lines
 * from and the same one the public page will serve, so what orders stores still
 * names exactly the content this patient was offered.
 *
 * The one edit that would break this silently is "the list is gone, so map over
 * something else" — or worse, over nothing. An empty `topicKeys` is not rejected
 * anywhere; it is accepted and stored as a truthful-looking record of an empty
 * offer. See rule 2 in this file's header.
 *
 * ORDER IS PRESERVED. The array is a record of what was published, in the order
 * a clinical author put it in, and re-sorting it destroys the one property that
 * makes it evidence rather than a set.
 */
export function safetyAckFor(safety: MedicationSafetyContent): SafetyAcknowledgement {
  return {
    version: safety.version,
    topicKeys: safety.topics.map((topic) => topic.key),
    source: 'patient',
  };
}

/**
 * WHY the pay control is blocked, in words, or null when it is not blocked.
 *
 * Lives here rather than in CheckoutFlow so the sentence and the content it
 * describes stay in one file, while the paragraph itself renders next to the
 * button it explains. The caller must render this text with
 * {@link PAY_BLOCKED_REASON_ID} whenever it is non-null.
 */
export function payBlockedReason(view: MedicationSafetyView, accepted: boolean): string | null {
  if (view.state === 'loading') {
    return 'Loading the safety information for your treatment — the order button unlocks once it is on screen.';
  }
  if (view.state === 'error' || view.safety === null) {
    // Deliberately honest and deliberately a dead end. There is no wording we
    // could put here instead that would make it safe to take money for a
    // prescription-only medicine whose risks we have failed to show.
    return 'We cannot show this treatment’s safety information right now, so we cannot take your order. Please contact us and we will sort this out with you.';
  }
  if (!accepted) {
    return 'Please confirm you have read and understood the risks and side effects to place your order.';
  }
  return null;
}

/**
 * The gate itself: the urgent lines, one route to the full detail, the Yellow
 * Card route, and the tick that unlocks the pay control.
 *
 * PURE, AND NOW STATELESS. The read lives at the view (CheckoutFlow), matching
 * the estate's "data loading lives at the view, not the leaf" rule — so
 * re-rendering the block can never refire the fetch. The `useState` that held
 * the modal open went with the modal, leaving no local state here at all: the
 * only thing this component remembers is what its caller passes it, which is why
 * the tick can be cleared by CheckoutFlow on the way back into this step.
 *
 * The tick is NOT persisted and is not restored on a hard refresh, exactly like
 * the commitment tick in `CommitmentTerms`. That is not an oversight: this is a
 * statement the patient makes at the moment they pay, and reinstating it from
 * storage would be the browser making it on their behalf.
 */
export function MedicationSafetyGate({
  view,
  accepted,
  onChange,
  onRetry,
}: {
  view: MedicationSafetyView;
  accepted: boolean;
  onChange: (next: boolean) => void;
  /** Retry the content read. Only meaningful in the `error` state. */
  onRetry?: () => void;
}) {
  if (view.state === 'loading') {
    return (
      <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', color: 'var(--gps-muted)' }}>
        Loading the safety information for your treatment…
      </p>
    );
  }

  // Both no-content branches say the SAME thing to the patient, because the same
  // thing is true for them either way: we have not shown them the risks, so we
  // are not taking their order. Only the recovery differs — a failed read gets a
  // Try again, a medication with no content on file cannot be fixed from here.
  if (view.state === 'error' || view.safety === null) {
    return (
      <Alert tone="warning" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
        <strong style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 'var(--gps-s-1)' }}>
          <WarningIcon size={15} />
          We can’t show your safety information right now
        </strong>
        <p style={{ margin: 0 }}>
          We won’t take your order until we have shown you what this treatment can do and what to watch for. Please
          contact us and we will sort this out with you.
        </p>
        {view.state === 'error' && onRetry ? (
          <Button
            type="button"
            variant="ghost"
            onClick={onRetry}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px', marginTop: 'var(--gps-s-2)' }}
          >
            Try again
          </Button>
        ) : null}
      </Alert>
    );
  }

  const safety = view.safety;

  return (
    <div
      // `gps-jux-panel`: the journey's one white card (tokens.css, P7).
      className="gps-jux-panel"
      style={{
        background: 'var(--gps-surface-2)',
        border: '1px solid var(--gps-border)',
        borderRadius: 'var(--gps-r-lg)',
        padding: 'var(--gps-s-4)',
        marginBottom: 'var(--gps-s-3)',
      }}
    >
      <p
        style={{
          margin: '0 0 10px',
          fontSize: '12px',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'var(--gps-muted)',
        }}
      >
        Before you pay — your treatment’s risks
      </p>

      {/* RULE 1. Rendered in place, in full, above everything else in this
          block. Never a disclosure, never truncated, never summarised — these
          are the lines that tell someone to stop taking the medicine and get
          help today, and they are worthless to a patient who has to press
          something to reach them. */}
      <PatientUrgentActions redFlags={safety.redFlags} />

      {/* RULE 2's VISIBLE HALF IS GONE, AND THE DATA IS NOT (2026-08-20). What
          stood here was a bulleted list of every topic title, kept in place so
          the recorded `topicKeys` described something the patient had actually
          seen. The CTO cut it for length. The keys did NOT go with it — they
          never came from this list, they come from the same `safety` object
          these urgent lines are drawn from (see `safetyAckFor`), so the
          acknowledgement still names every topic on offer.

          Anyone shortening this block further should know which of the two is
          load-bearing: the list was presentation, the keys are the record orders
          keeps of what this order was sold against. */}

      {/* THE ONE CONTROL. One clearly-labelled route to the whole detail,
          replacing the modal-opening button and the second "Read more" link that
          used to sit beside it. Two adjacent controls that both lead to the same
          clinical content, differing only in where the words were assembled, is
          a choice no patient can make — and only one of them had an address.

          AN ANCHOR WITH A REAL `href`, NEVER A BUTTON CALLING `window.open`. A
          real URL can be middle-clicked, copied, opened in a new window, kept
          for later, or followed with JavaScript disabled. None of that was true
          of the dialog this replaces, and all of it matters for a page a patient
          may want to re-read, or show someone, after they have paid.

          `target="_blank"` because the alternative is navigating a patient OUT
          of a half-finished checkout to read the risks — a reliable way to make
          reading them cost the order, which is exactly the trade the CTO's
          "don't hinder the journey flow" rules out.

          `rel="noreferrer"` is not boilerplate here. This checkout URL can carry
          journey identifiers; `noreferrer` withholds it from the destination AND
          severs `window.opener` with it, so the opened tab cannot steer the
          checkout it came from.

          THE NEW-TAB WARNING IS IN THE VISIBLE TEXT, so it is part of the
          accessible NAME (WCAG 2.1 AA 3.2.5 in substance): a screen-reader user
          is told the context is about to change BEFORE they follow it, rather
          than discovering it once their focus has landed somewhere they did not
          ask to be. Never an icon — an icon announces nothing.

          THE PATH COMES FROM THE ROUTE CONSTANT, never a hand-typed string. Four
          places have to agree on it and one re-typing produces the single worst
          dead link this portal can serve — a safety page that 404s from the
          payment screen. See safety/route.ts. */}
      {/* A TEXT LINK, DELIBERATELY NOT A BUTTON (CTO 2026-09-12). It used to wear
          buttonStyle('primary') — the same chrome as the checkout's own primary
          action — so patients read it as the way to proceed and clicked it instead
          of paying. Two primary buttons on one screen is one call to action too
          many, and the one that should win is the one that completes the order.
          Styling it as a link costs nothing: same href, same new-tab warning inside
          the accessible name, same rel — it just looks like what it is, somewhere to
          read rather than the way forward. It now matches the Yellow Card link below,
          the other read-more on this screen. `display: inline-block` with vertical
          padding keeps a comfortable pointer target now the button's 44px min-height
          is gone. Do NOT restore button chrome here. */}
      <div style={{ marginBottom: 'var(--gps-s-3)' }}>
        <a
          // The route CONSTANT, prefixed with the served basename (config.ts) because
          // this is a raw anchor to a new tab, which react-router cannot prefix.
          href={withBasename(CLINICAL_SAFETY_PATH)}
          target="_blank"
          rel="noreferrer"
          style={{
            color: 'var(--gps-link)',
            textDecoration: 'underline',
            display: 'inline-block',
            padding: '6px 0',
            fontSize: '14px',
            lineHeight: 1.5,
          }}
        >
          Read the full safety information (opens in a new tab)
        </a>
      </div>

      {/* The patient's OWN route to report a side effect, not ours. It belongs
          on this screen rather than only in the dispatch email: by the time
          something happens they will not go looking for the email that sold
          them the plan. */}
      <PatientYellowCardLine yellowCardUrl={safety.yellowCardUrl} />

      {/* RULE 4's other half — a REAL checkbox with a REAL label, not a styled
          div: it has to be reachable and toggleable from the keyboard and
          announce its own checked state. Structure copied from CommitmentTerms
          on purpose; the vertical padding is what carries the row to a 44px
          target without moving the tick off the first line of its label. */}
      <label
        style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          cursor: 'pointer',
          fontSize: '14px',
          lineHeight: 1.5,
          minHeight: '44px',
          padding: '6px 0',
        }}
      >
        <input
          type="checkbox"
          checked={accepted}
          onChange={(e) => onChange(e.target.checked)}
          aria-describedby="medication-safety-ack-detail"
          style={{ width: '18px', height: '18px', flex: 'none', marginTop: '2px', accentColor: 'var(--gps-primary)', cursor: 'pointer' }}
        />
        <span id="medication-safety-ack-detail">
          I have read and understood the risks and side effects of this treatment, including when to get urgent medical
          help.
        </span>
      </label>
    </div>
  );
}

/**
 * THE URGENT-ACTION BLOCK, exactly as the payment screen draws it.
 *
 * EXTRACTED, NOT COPIED (2026-08-20). The public clinical-safety page has to
 * show these lines too, and the CTO's brief was specific: that page is to carry
 * this block, not something that resembles it. A second implementation would
 * look identical on the day it was written and drift on the first restyle —
 * and the thing that would drift is the only wording on either screen that
 * tells somebody to stop taking a medicine and get help today.
 *
 * So the gate mounts this, and so does the page. Whatever one shows, the other
 * shows.
 *
 * The bordered box is doing accessible work, not decoration: the border colour
 * ALONE never carries the meaning (WCAG 2.1 AA 1.4.1) — the heading says what
 * the lines are for, and the list has its own accessible name so a screen-reader
 * user who lands mid-page knows what they are in.
 */
export function PatientUrgentActions({ redFlags }: { redFlags: string[] }) {
  return (
    <div
      style={{
        border: '1px solid var(--gps-danger-text)',
        borderLeftWidth: '4px',
        borderRadius: 'var(--gps-r-md)',
        padding: 'var(--gps-s-3)',
        marginBottom: 'var(--gps-s-3)',
      }}
    >
      <strong
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '14.5px',
          marginBottom: 'var(--gps-s-2)',
          color: 'var(--gps-text)',
        }}
      >
        <WarningIcon size={15} />
        Get medical help straight away if any of these happen
      </strong>
      <ul
        aria-label="When to get urgent medical help"
        style={{ margin: 0, paddingLeft: '18px', fontSize: '14px', lineHeight: 1.55, color: 'var(--gps-text)' }}
      >
        {redFlags.map((line) => (
          <li key={line} style={{ marginBottom: '6px' }}>
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Every topic, in the order the server sent them, each with its paragraphs.
 *
 * SPLIT OUT OF {@link PatientSafetyDetail} (2026-08-20) so the public
 * clinical-safety page can pair the topics with the BOXED urgent block above
 * rather than the plainer one `PatientSafetyDetail` uses — without either
 * surface owning a second copy of the topic markup.
 *
 * NOT CALLED FROM THIS FILE, AND NOT DEAD. Its consumer is
 * safety/ClinicalSafetyPage.tsx. It lives here because this module is where the
 * patient-facing safety markup is written ONCE; moving it next to its only
 * current caller is the first step back to a per-screen copy, and two renderers
 * of the same clinical prose are two things that can disagree with nobody able
 * to say which one a patient read.
 *
 * THE ORDER IS NOT OURS TO CHANGE, and neither is the paragraph split. Both are
 * clinical authoring decisions: re-sorting topics moves what a reader meets
 * first, and joining paragraphs runs two separate statements into one sentence.
 *
 * `h4` is deliberate and fixed. Every consumer nests these under an `h3` (the
 * clinician review's per-treatment heading, the public page's per-medication
 * block), so the document outline stays unbroken — which is how a screen-reader
 * user navigates a long page of clinical prose.
 */
export function PatientSafetyTopics({ topics }: { topics: MedicationSafetyTopic[] }) {
  return (
    <>
      {topics.map((topic) => (
        <section key={topic.key} style={{ marginBottom: 'var(--gps-s-4)' }}>
          <h4 style={{ margin: '0 0 6px', fontSize: '15px', color: 'var(--gps-text)' }}>{topic.title}</h4>
          {topic.body.map((paragraph) => (
            <p key={paragraph} style={{ margin: '0 0 8px' }}>
              {paragraph}
            </p>
          ))}
        </section>
      ))}
    </>
  );
}

/**
 * The full detail — urgent lines first, then every topic with its paragraphs.
 *
 * NO LONGER MOUNTED BY THE GATE (2026-08-20), and deliberately kept. It was the
 * body of the checkout modal; that modal is gone, and the payment screen now
 * sends patients to the public page instead. Its remaining consumer is
 * clinicians/SafetyReview.tsx, where the clinical lead ratifies this wording
 * before it goes near a patient — he is signing off WHAT A PATIENT SEES, so the
 * review surface has to mount the patient's renderer rather than one of its own.
 *
 * DELETING IT BECAUSE THE GATE STOPPED CALLING IT would blank the clinical
 * sign-off screen, which is not a screen anyone editing checkout has open.
 *
 * NAMED FOR ITS AUDIENCE. The name says WHOSE view this is, so the import site
 * on a staff screen reads as "the patient's detail" rather than as a generic
 * list; the old `SafetyTopics` also undersold it, since it renders the urgent
 * lines too, and those are the lines a reviewer most needs to see.
 */
export function PatientSafetyDetail({ safety }: { safety: MedicationSafetyContent }) {
  return (
    <div style={{ fontSize: '14px', lineHeight: 1.6, color: 'var(--gps-text)' }}>
      <strong style={{ display: 'block', marginBottom: 'var(--gps-s-2)' }}>
        Get medical help straight away if any of these happen
      </strong>
      <ul style={{ margin: '0 0 var(--gps-s-4)', paddingLeft: '18px' }}>
        {safety.redFlags.map((line) => (
          <li key={line} style={{ marginBottom: '6px' }}>
            {line}
          </li>
        ))}
      </ul>
      <PatientSafetyTopics topics={safety.topics} />
    </div>
  );
}

/**
 * The MHRA Yellow Card sentence, worded exactly as the payment screen words it.
 *
 * Pulled out of the gate's JSX for the same reason the detail renderer is
 * exported: the review surface has to show a clinician the reporting route the
 * PATIENT is handed, wording included. Re-typing this sentence on the review
 * screen would create a second copy that nobody is obliged to keep in step with
 * this one — and "your clinician knows" is a promise about our process, so the
 * clinician reviewing it should be reading the promise we actually make.
 */
export function PatientYellowCardLine({ yellowCardUrl }: { yellowCardUrl: string }) {
  return (
    <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
      You can report a suspected side effect yourself through the MHRA{' '}
      <a href={yellowCardUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--gps-link)' }}>
        Yellow Card scheme
      </a>{' '}
      (opens in a new tab). Tell us as well, so your clinician knows.
    </p>
  );
}

/**
 * The independent sources for THIS medicine — the NHS page, the MHRA leaflet,
 * whatever the clinical author judged a patient should be able to go and read.
 *
 * WHY IT LIVES IN THIS FILE rather than beside the page that currently mounts
 * it. This module is where the PATIENT-FACING safety renderers live, by design:
 * the payment screen, the clinician review surface and the public clinical-safety
 * page all draw from here, so no two of them can show different words. Putting
 * this one renderer somewhere else would be the first step back to a per-screen
 * copy — and the next surface that needs independent sources must mount THIS
 * component rather than write a second one.
 *
 * NOT CALLED FROM THIS FILE. Its consumer is safety/ClinicalSafetyPage.tsx —
 * which, since 2026-08-20, is where the payment screen's one control sends
 * people, so these links are part of what the checkout acknowledgement now
 * attests was on offer.
 *
 * RENDERS NOTHING when the list is absent or empty. Absent is a normal state
 * (`externalLinks` is optional in contracts v0.10.0), and an empty "Read more
 * about this medicine" heading with no links under it reads as a broken page.
 *
 * EVERY LINK SAYS IT LEAVES US, IN TEXT. "(opens in a new tab)" is inside the
 * anchor, so it is part of the accessible NAME rather than a visual-only cue —
 * WCAG 2.1 AA 3.2.5 in substance: a screen-reader user is told the context is
 * about to change before they activate it, not after their focus has vanished.
 *
 * `rel="noreferrer"` on every one, and it is not boilerplate. It withholds this
 * page's URL from the destination AND severs `window.opener`, so a page we do
 * not control cannot steer the tab the reader came from. These are NHS and MHRA
 * addresses today; the field is server-supplied, and the guarantee has to hold
 * for whatever it contains tomorrow.
 */
export function PatientExternalSources({ links }: { links?: MedicationSafetyExternalLink[] }) {
  if (!links || links.length === 0) return null;
  return (
    <div style={{ marginTop: 'var(--gps-s-3)' }}>
      <h4 style={{ margin: '0 0 6px', fontSize: '15px', color: 'var(--gps-text)' }}>
        Independent information about this treatment
      </h4>
      <p style={{ margin: '0 0 8px', fontSize: '13.5px', lineHeight: 1.5, color: 'var(--gps-text)' }}>
        These pages are written by the NHS and the MHRA, not by us.
      </p>
      <ul
        aria-label="Independent sources about this treatment"
        style={{ margin: 0, paddingLeft: '18px', fontSize: '14px', lineHeight: 1.6, color: 'var(--gps-text)' }}
      >
        {links.map((link) => (
          <li key={link.url} style={{ marginBottom: '6px' }}>
            <a href={link.url} target="_blank" rel="noreferrer" style={{ color: 'var(--gps-link)' }}>
              {link.label} (opens in a new tab)
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
